/** The vocabulary of a recurrence rule, as the screens need it.
 *
 *  Pure, and in `lib` rather than in the page, for two reasons. It is the frontend's
 *  half of a contract with `RecurrenceRule.validate` in the backend and deserves to
 *  be findable on its own. And it is testable in Node this way: importing the page
 *  pulls in `lib/auth`, which reads `window.location` at module load, so anything
 *  living there needs a DOM to reach.
 */
export const FREQ = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY']

export const MONTH: Record<number, string> = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
}

/** The noun that follows "every 2". */
export const EVERY_UNIT: Record<string, string> = {
  DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month',
  QUARTERLY: 'quarter', YEARLY: 'year',
}

/** Which fields each frequency actually uses, mirroring `RecurrenceRule.validate`.
 *
 *  Written out rather than inferred, because the editor used to guess: anything that
 *  was not WEEKLY got a "Day of month" box, so a DAILY rule asked which day of the
 *  month it repeated on - a question with no meaning that the server then ignored.
 *  QUARTERLY and YEARLY need months too, and nothing offered an interval at all, so
 *  "every 2 weeks" was unreachable from the screen even though `describe()` renders
 *  it and the server has always accepted it.
 */
export const NEEDS: Record<string, {
  interval: boolean; days: boolean; dayOfMonth: boolean; months: boolean
}> = {
  DAILY: { interval: true, days: false, dayOfMonth: false, months: false },
  WEEKLY: { interval: true, days: true, dayOfMonth: false, months: false },
  MONTHLY: { interval: true, days: false, dayOfMonth: true, months: false },
  // No interval for these two. They compile to a monthly rrule with a month filter,
  // so an interval counts months and is applied *before* the filter: with months
  // 1,4,7,10 from an August start, 2 gives half the occurrences and 3 gives none at
  // all. The months list is what sets the period, and the server refuses anything
  // else now - offering the box would be offering a way to build a dead rule.
  QUARTERLY: { interval: false, days: false, dayOfMonth: true, months: true },
  YEARLY: { interval: false, days: false, dayOfMonth: true, months: true },
}

/** What the fields become when the frequency changes.
 *
 *  Switching cleared nothing, so a rule moved from MONTHLY to DAILY kept a day of
 *  month the server stores and ignores, and one moved to QUARTERLY arrived with no
 *  months at all - which fails validation immediately, so the live preview showed an
 *  error before the person had done anything wrong. A field that is not used is
 *  emptied, and one that is now required gets a sensible starting value.
 */
type Tuned = {
  frequency: string; interval: number; days_of_week: string
  day_of_month: string; months_of_year: string
}

export const retune = <T extends Tuned>(form: T, frequency: string): T => {
  const needs = NEEDS[frequency] ?? NEEDS.MONTHLY
  return {
    ...form,
    frequency,
    // Back to 1 where an interval has no meaning, so a 3 left over from MONTHLY
    // cannot follow the person into QUARTERLY and be refused on save.
    interval: needs.interval ? (Number(form.interval) || 1) : 1,
    days_of_week: needs.days ? (form.days_of_week || 'MON') : '',
    day_of_month: needs.dayOfMonth ? (form.day_of_month || '20') : '',
    months_of_year: needs.months
      ? (form.months_of_year || (frequency === 'YEARLY' ? '4' : '1,4,7,10'))
      : '',
  }
}
/** What to do when an occurrence lands on a non-working day. No default, by
 *  design: the system will not guess whether a deadline moves earlier or later. */
export const NWD = ['BEFORE', 'AFTER', 'SKIP']

/** The Repeats column, in words.
 *
 *  It used to write "every 2 month on day 20" - the unit never pluralised - and a
 *  weekly rule read just "every 2 week", never saying which days, so two rules that
 *  differ only in their days were indistinguishable in the list.
 */
export const describe = (r: {
  frequency: string; interval: number
  // Nullable because that is how the list type spells them. The API coerces them to
  // text now, but a type that only accepted strings would make this function lie
  // about what it can be handed.
  day_of_month?: string | null
  days_of_week?: string | null
  months_of_year?: string | null
}) => {
  const n = NEEDS[r.frequency]?.interval ? (Number(r.interval) || 1) : 1
  const unit = EVERY_UNIT[r.frequency] ?? 'period'
  const every = n > 1 ? `every ${n} ${unit}s` : `every ${unit}`
  const parts = [every]
  if (NEEDS[r.frequency]?.days && r.days_of_week) {
    parts.push(`on ${r.days_of_week.split(',').map((d) => d.trim()).join(', ')}`)
  }
  if (NEEDS[r.frequency]?.dayOfMonth && r.day_of_month) {
    parts.push(String(r.day_of_month).toUpperCase() === 'LAST'
      ? 'on the last day' : `on day ${r.day_of_month}`)
  }
  if (NEEDS[r.frequency]?.months && r.months_of_year) {
    parts.push(`in ${r.months_of_year.split(',').map((m) => MONTH[Number(m.trim())]
      ?? m.trim()).join(', ')}`)
  }
  return parts.join(' ')
}


// ------------------------------------------------------------ pickers, not typing
//
// Every one of these used to be a text box: "MON,THU", "20 or LAST", "1,4,7,10".
// That asks somebody to know a format, and gets it wrong in ways the server can only
// refuse after the fact - "'MONDAY' is not a weekday" is a real error message it
// could produce. The values still travel as CSV, because that is what the API takes;
// what changes is that nobody types them.

/** In the order a week is read, not the order the parser accepts them in. */
export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

/** 1..12, paired with a short name. */
export const MONTHS = Object.keys(MONTH).map(Number).sort((a, b) => a - b)

/** 1..31, then LAST - which is a real answer and not a number, so it cannot be a
 *  numeric input. "The last day of the month" is how a filing deadline is usually
 *  written, and February is why it matters. */
export const DAYS_OF_MONTH = [
  ...Array.from({ length: 31 }, (_, i) => String(i + 1)), 'LAST',
]

/** How many periods between occurrences. Beyond a year is not a recurrence anybody
 *  writes; the cap is what lets this be a list rather than free entry. */
export const INTERVALS = Array.from({ length: 12 }, (_, i) => i + 1)

const parts = (csv?: string | null) =>
  String(csv ?? '').split(',').map((p) => p.trim()).filter(Boolean)

/** Whether a CSV field already holds this value. */
export const inCsv = (csv: string | null | undefined, value: string | number) =>
  parts(csv).includes(String(value))

/** Add or remove one value from a CSV field, keeping a stable order.
 *
 *  `order` matters: without it the days come out in the sequence they were clicked,
 *  so "THU,MON" and "MON,THU" are the same rule spelled two ways - which reads
 *  wrongly in the list and makes two identical rules look different.
 */
export const toggleCsv = (
  csv: string | null | undefined, value: string | number,
  order: (string | number)[],
) => {
  const have = parts(csv)
  const v = String(value)
  const next = have.includes(v) ? have.filter((p) => p !== v) : [...have, v]
  const rank = new Map(order.map((o, i) => [String(o), i]))
  return next
    .sort((a, b) => (rank.get(a) ?? 99) - (rank.get(b) ?? 99))
    .join(',')
}
